import { useParams, Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articlesBySlug, articles } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

const baseUrl = 'https://haosexiaoyi.com';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? articlesBySlug[slug] : undefined;

  if (!article) {
    return (
      <>
        <SEO
          title="文章未找到"
          description="抱歉，您查找的文章不存在。请浏览我们的其他精彩内容。"
          keywords="好色小姨,都市言情,文章"
          canonicalUrl={`${baseUrl}/article/${slug || ''}`}
        />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16 bg-background-50">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您查找的文章不存在。</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </>
    );
  }

  const category = categoryMap[article.category];
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: article.title,
        description: article.excerpt,
        image: article.image,
        author: {
          '@type': 'Person',
          name: article.author,
        },
        datePublished: article.date,
        dateModified: article.date,
        publisher: {
          '@type': 'Organization',
          name: '好色小姨故事集',
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${baseUrl}/article/${slug}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: baseUrl },
          ...(category ? [{ '@type': 'ListItem', position: 2, name: category.name, item: `${baseUrl}/category/${article.category}` }] : []),
          { '@type': 'ListItem', position: category ? 3 : 2, name: article.title, item: `${baseUrl}/article/${slug}` },
        ],
      },
    ],
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(category ? [{ label: category.name, href: `/category/${article.category}` }] : []),
    { label: article.title },
  ];

  return (
    <>
      <SEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${baseUrl}/article/${slug}`}
        ogType="article"
        ogImage={article.image}
        schema={schema}
      />
      <Navbar />
      <main className="pt-20 pb-14 md:pb-20 bg-background-50 min-h-screen">
        <article className="w-full max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="mb-8">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-accent-100/70 text-accent-600">
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 leading-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {article.title}
            </h1>
            <div className="flex items-center gap-3 text-sm text-foreground-500">
              <span>{article.author}</span>
              <span>·</span>
              <span>{article.date}</span>
              <span>·</span>
              <span>{article.readTime}</span>
            </div>
          </header>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.imageAlt}
              className="w-full aspect-[16/9] object-cover"
              title={article.title}
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100/80 text-secondary-700">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full max-w-4xl mx-auto px-4 md:px-6 mt-16">
            <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
              相关文章
            </h2>
            <div className="space-y-4">
              {relatedArticles.map((ra) => (
                <ArticleCard key={ra.slug} article={ra} variant="default" />
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}